import React from "react";
import IUserContext from "../../../infrastructure/context/user/user.context.interface";
import CheckoutAddressForm, {
  CheckoutAddressFormRefs,
} from "../common/address-form/checkout-address-form";
import Input from "./../../common/input";
import CheckoutAddressSelectionCheckbox from "../common/checkout-address-selection-checkbox";
import { Address } from "../../../infrastructure/http/modules/cart/cart.models";
import CheckUtils from "../../../utilities/check-utils";
import TranslationsContext from "../../../infrastructure/context/translations/translations-context";
import ITranslationsContext from "../../../infrastructure/context/translations/translations-context.interface";
import TranslationManager from "../../../infrastructure/translation-manager";
import { TranslationKeys } from "../../../infrastructure/const";
import ConfigurationContext from "../../../infrastructure/context/configuration/configuration.context";
import { ErrorResponse } from "../adyen/checkout-adyen";
import CacheManager from "../../../infrastructure/cache/cache-manager";
import CacheKeys from "../../../infrastructure/cache/cache-keys";

type CheckoutAddressProps = {
  userContext: IUserContext;
  onDeliveryAddressChange: (address: Address | null) => void;
  onInvoiceAddressChange: (address: Address) => void;
  displayValidationMessages: boolean;
  apiErrorResponse: ErrorResponse;
  invoiceAddress: Address | null;
  deliveryAddress: Address | null;
  specialInformation: string;
  onSaveInvoiceAddressChange: (saveAddress: boolean) => void;
  onSaveDeliveryAddressChange: (saveAddress: boolean) => void;
  onSpecialInformationChange: (specialInformation: string) => void;
};

type CheckoutAddressState = {
  deliveryAddress: Address | null;
  invoiceAddress: Address;
  displayDeliveryAddessForm: boolean;
  saveInvoiceAddress: boolean;
  saveDeliveryAddress: boolean;
  specialInformation: string;
};

export default class CheckoutAddress extends React.Component<
  CheckoutAddressProps,
  CheckoutAddressState
> {
  private invoiceAddressRef: React.RefObject<CheckoutAddressFormRefs> | null =
    null;
  private deliveryAddressRef: React.RefObject<CheckoutAddressFormRefs> | null =
    null;
  private TranslationsManager: TranslationManager;
  private translations: { [id: string]: string };

  constructor(props: CheckoutAddressProps, context: ITranslationsContext) {
    super(props, context);
    const shipToDifferentAddress = CacheManager.getItem(
      CacheKeys.shipToDifferentAddress
    );

    this.state = {
      invoiceAddress: CheckUtils.isNullObject(this.props.invoiceAddress)
        ? new Address()
        : (this.props.invoiceAddress as Address),
      deliveryAddress: CheckUtils.isNullObject(this.props.deliveryAddress)
        ? null
        : (this.props.deliveryAddress as Address),
      displayDeliveryAddessForm: CheckUtils.isNullObject(shipToDifferentAddress)
        ? false
        : (shipToDifferentAddress as unknown as boolean),
      saveInvoiceAddress: false,
      saveDeliveryAddress: false,
      specialInformation: this.props.specialInformation,
    };

    this.TranslationsManager = new TranslationManager(context);
    this.translations = {
      deliveryInformation: this.TranslationsManager.getTranslation(
        TranslationKeys.CHECKOUT_DELIVERYINFORMATION_TITLE
      ),
      specialInformation: this.TranslationsManager.getTranslation(
        TranslationKeys.CHECKOUT_SPECIAL_INFORMATION
      ),
    };

    this.invoiceAddressRef = React.createRef();
    this.deliveryAddressRef = React.createRef();
  }

  onAddressSelectionChange(isDeliveryDisplayed: boolean) {
    CacheManager.setItem(
      CacheKeys.shipToDifferentAddress,
      isDeliveryDisplayed as unknown as object
    );
    if (!isDeliveryDisplayed) {
      CacheManager.removeItem(CacheKeys.deliveryAddressKey);
      this.setState({ saveDeliveryAddress: false });
      this.onDeliveryAddressChange(null);
      this.onSpecialInformationChange('');
    }
    this.setState({
      displayDeliveryAddessForm: isDeliveryDisplayed,
      deliveryAddress: isDeliveryDisplayed ? new Address() : null,
    });
  }

  onDeliveryAddressChange(address: Address | null) {
    this.setState({ deliveryAddress: address });
    this.props.onDeliveryAddressChange(address);
  }

  onInvoiceAddressChange(address: Address) {
    this.setState({ invoiceAddress: address });
    this.props.onInvoiceAddressChange(address);
  }

  onSpecialInformationChange(specialInformation: string) {
    this.setState({ specialInformation: specialInformation });
    this.props.onSpecialInformationChange(specialInformation);
  }

  isComponentFilledCorrectly(): boolean {

    const invoiceAddressValid = 
      !CheckUtils.isNullOrEmptyString(this.state.invoiceAddress.lastName) &&
      !CheckUtils.isNullOrEmptyString(this.state.invoiceAddress.addressLine1) && 
      !CheckUtils.isNullOrEmptyString(this.state.invoiceAddress.postalCode) &&
      !CheckUtils.isNullOrEmptyString(this.state.invoiceAddress.city);

    const deliveryAddressValid = !this.state.deliveryAddress || (
      !CheckUtils.isNullOrEmptyString(this.state.deliveryAddress.lastName) &&
      !CheckUtils.isNullOrEmptyString(this.state.deliveryAddress.addressLine1) && 
      !CheckUtils.isNullOrEmptyString(this.state.deliveryAddress.postalCode) &&
      !CheckUtils.isNullOrEmptyString(this.state.deliveryAddress.city));

    return (
      invoiceAddressValid && deliveryAddressValid
    );
  }

  render() {
    return (
      <ConfigurationContext.Consumer>
        {(configurationContext) => (
          <>
            <li id="opc-billing">
              <div id="checkout-step-billing" className="checkout-step">
                <div className="checkout-step__title">
                  <span className="text">
                    {this.translations.deliveryInformation}
                  </span>
                </div>
                <div className="content-container">
                  <form className="form-validate">
                    <CheckoutAddressForm
                      ref={this.invoiceAddressRef}
                      displayEmailAddress={false}
                      address={this.state.invoiceAddress}
                      onUpdate={(address: Address) =>
                        this.onInvoiceAddressChange(address)
                      }
                      displayValidationMessages={
                        this.props.displayValidationMessages
                      }
                      defaultAddressId={null}
                      supportedCountries={
                        configurationContext.supportedCountries
                      }
                      apiErrorResponse={
                        CheckUtils.isNullObject(this.props.apiErrorResponse)
                          ? []
                          : this.props.apiErrorResponse.invoiceAddressErrors
                      }
                      saveAddress={this.state.saveInvoiceAddress}
                      onSaveAddressChanged={(newValue: boolean) => {
                        this.setState({ saveInvoiceAddress: newValue });
                        this.props.onSaveInvoiceAddressChange(newValue);
                      }}
                      isInvoiceAddress={true}
                      fields={["lastName"]}
                    />                    
                    <CheckoutAddressSelectionCheckbox
                      isDifferentAddress={this.state.displayDeliveryAddessForm}
                      onChange={(newValue) =>
                        this.onAddressSelectionChange(newValue)
                      }
                    />
                  </form>
                </div>
              </div>
            </li>
            <li id="opc-shipping">
              <div id="checkout-step-shipping" className="checkout-step">
                <div className="content-container">
                  <form className="different-address-form-validate">
                    {this.state.displayDeliveryAddessForm &&
                      !CheckUtils.isNullObject(this.state.deliveryAddress) && (
                        <>
                        <CheckoutAddressForm
                          ref={this.deliveryAddressRef}
                          displayEmailAddress={false}
                          address={this.state.deliveryAddress as Address}
                          onUpdate={(address: Address) =>
                            this.onDeliveryAddressChange(address)
                          }
                          displayValidationMessages={
                            this.props.displayValidationMessages
                          }
                          defaultAddressId={null}
                          supportedCountries={
                            configurationContext.supportedCountries
                          }
                          apiErrorResponse={
                            CheckUtils.isNullObject(this.props.apiErrorResponse)
                              ? []
                              : this.props.apiErrorResponse
                                  .deliveryAddressErrors
                          }
                          saveAddress={this.state.saveDeliveryAddress}
                          onSaveAddressChanged={(newValue: boolean) => {
                            this.setState({ saveDeliveryAddress: newValue });
                            this.props.onSaveDeliveryAddressChange(newValue);
                          }}
                          isInvoiceAddress={false}
                          fields={["lastName"]}
                        />

                        <div>
                          <li className="input-container">
                            <Input
                              label={this.translations.specialInformation}
                              name="SpecialInformation"
                              value={this.state.specialInformation}
                              onValueChange={(name: string, value: string, valid: boolean) => {
                                this.onSpecialInformationChange(value);
                              }}
                            />
                          </li>
                        </div>                        
                        </>                        
                      )}
                  </form>
                </div>
              </div>
            </li>
          </>
        )}
      </ConfigurationContext.Consumer>
    );
  }
}

CheckoutAddress.contextType = TranslationsContext;
